/**
 * §P.3 · Tarifas y cuotas.
 *
 * **Todo el dinero en céntimos enteros.** 25,00 € es `2500`. El campo del
 * formulario enseña euros y manda enteros: `0.1 + 0.2` no es `0.3`, y en una
 * remesa de 150 recibos eso se acumula.
 */

export type Periodicidad = 'monthly' | 'quarterly' | 'biannual' | 'annual';

export interface Tarifa {
  readonly id: string;
  readonly name: string;
  readonly amount_cents: number;
  readonly currency: string;
  readonly period: string;
  readonly discipline_id: string | null;
  readonly sessions_per_week: number | null;
  /** `false` es retirada. **No borrada**: los recibos viejos siguen apuntando a ella. */
  readonly active: boolean;
}

export type EstadoDeCuota = 'active' | 'paused' | 'cancelled';

export interface Cuota {
  readonly id: string;
  readonly student_id: string;
  readonly payer_id: string;
  readonly fee_plan_id: string;
  /** Un precio pactado que sustituye al de la tarifa. */
  readonly custom_amount_cents: number | null;
  readonly starts_on: Date | string;
  readonly ends_on: Date | string | null;
  readonly status: string;
  readonly paused_from: Date | string | null;
  readonly paused_to: Date | string | null;
}

export interface PaginaDeCuotas {
  readonly data: readonly Cuota[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/**
 * El desglose de «cuánto paga hoy y por qué».
 *
 * Es la pantalla que se abre cuando una familia llama preguntando por su recibo,
 * así que el **por qué** importa tanto como el cuánto: de dónde sale la base, qué
 * descuentos se aplican y si hay un precio pactado que manda sobre la tarifa.
 */
export interface Desglose {
  readonly subscription_id: string;
  readonly status: string;
  readonly fee_plan: string | null;
  readonly base_cents: number;
  readonly percent_off: number | null;
  readonly fixed_off_cents: number | null;
  readonly amount_cents: number;
  readonly discount_codes: readonly string[] | null;
  readonly agreed_price: boolean | null;
}

export const PERIODICIDADES: readonly {
  valor: Periodicidad;
  etiqueta: string;
}[] = [
  { valor: 'monthly', etiqueta: 'Mensual' },
  { valor: 'quarterly', etiqueta: 'Trimestral' },
  { valor: 'biannual', etiqueta: 'Semestral' },
  { valor: 'annual', etiqueta: 'Anual' },
];

export const etiquetaDePeriodo = (period: string): string =>
  PERIODICIDADES.find((p) => p.valor === period)?.etiqueta ?? period;

/**
 * Euros escritos a mano → céntimos enteros.
 *
 * Acepta lo que la gente teclea de verdad: `25`, `25,50`, `25.50`, `1.234,56`. Y
 * **no usa coma flotante para el redondeo**: parte la cadena por el separador
 * decimal, porque `Math.round(25.545 * 100)` da 2554 y no 2555 en algunos casos.
 *
 * Devuelve `null` si no se entiende, para que el formulario no mande basura.
 */
export function aCentimos(texto: string): number | null {
  const limpio = texto.trim().replace(/\s/g, '').replace(/€/g, '');
  if (limpio === '') return null;

  // Si hay coma y punto, el último es el decimal y el otro es de millares.
  const ultimaComa = limpio.lastIndexOf(',');
  const ultimoPunto = limpio.lastIndexOf('.');
  const corte = Math.max(ultimaComa, ultimoPunto);

  const enteroTexto = corte === -1 ? limpio : limpio.slice(0, corte);
  const decimalTexto = corte === -1 ? '' : limpio.slice(corte + 1);

  /*
   * Los separadores de millares **agrupan de tres en tres**, y comprobarlo no es
   * quisquillosidad: sin ello, `25,5,5` se leía como «255,50». Se colaba porque
   * la regla «el último separador es el decimal» es correcta y no basta.
   */
  const grupos = enteroTexto.split(/[.,]/);
  if (grupos.length > 1) {
    const primero = grupos[0]!;
    if (!/^\d{1,3}$/.test(primero)) return null;
    if (!grupos.slice(1).every((g) => /^\d{3}$/.test(g))) return null;
  }

  const entero = enteroTexto.replace(/[.,]/g, '');
  if (!/^\d*$/.test(entero) || !/^\d*$/.test(decimalTexto)) return null;
  if (entero === '' && decimalTexto === '') return null;
  // Más de dos decimales no es un importe, es un error de tecleo.
  if (decimalTexto.length > 2) return null;

  const centimos = decimalTexto.padEnd(2, '0');
  return (
    Number.parseInt(entero || '0', 10) * 100 + Number.parseInt(centimos, 10)
  );
}

/** Céntimos → lo que se pone en un campo de texto para editarlo. */
export const aEuros = (centimos: number): string =>
  (centimos / 100).toFixed(2).replace('.', ',');

/**
 * El estado de una cuota, en español.
 *
 * Existe porque un `active` crudo se escapó a la ficha del pagador y se leía
 * «Cuota active». El servidor manda el enum en inglés —es el contrato— y la
 * traducción es cosa de la pantalla, así que vive en un solo sitio.
 */
export const estadoDeCuotaEnEspanol = (status: string): string =>
  ({ active: 'activa', paused: 'pausada', cancelled: 'cancelada' })[status] ??
  status;
